import { Ref, computed } from 'vue';
import { useRoute } from 'vue-router';

import useAuth from '@/composables/useAuth';
import { GENERAL_ACCESS_ONLY_INVITED } from '@/constants/reports';
import { ReportAccessLevel } from '@/types/report_team_access';
import { Report } from '@/types/reports';

export function getReportIsOwner(
  report: Partial<Report>,
  userId: number | undefined,
  orgId: number | undefined,
): boolean {
  return (
    report?.created_by_user_id === userId && report?.organization_id === orgId
  );
}

export function getReportUserAccessLevel(
  report: Partial<Report>,
  userId: number | undefined,
  orgId: number | undefined,
): ReportAccessLevel | null {
  const specificAccess =
    report.user_access?.find(
      (a) =>
        a.user_id === userId &&
        (a.organization_id == null || a.organization_id === orgId),
    )?.access_level ?? null;

  if (specificAccess) return specificAccess;

  if (
    report.general_access &&
    report.general_access !== GENERAL_ACCESS_ONLY_INVITED
  ) {
    return report.general_access;
  }

  return null;
}

export const useReportPermissions = (report: Ref<Partial<Report>>) => {
  const { currentOrg, currentUser, hasScope, isAdmin } = useAuth();
  const route = useRoute();

  const isPublicReport = computed(() => report.value?.visibility === 'public');

  const currentUserAccess = computed(() => {
    if (!report.value?.user_access || !currentUser.value?.id) {
      return null;
    }
    return report.value.user_access.find(
      (access) =>
        access.user_id === currentUser.value?.id &&
        (access.organization_id == null ||
          access.organization_id === currentOrg.value?.id),
    );
  });

  const currentUserId = computed(() => currentUser.value?.id);
  const currentOrgId = computed(() => currentOrg.value?.id);

  const isReportSaved = computed(
    () => !!report.value?.id || !!route.params?.report_id,
  );

  const userAccessLevel = computed<ReportAccessLevel | null>(() =>
    getReportUserAccessLevel(
      report.value,
      currentUserId.value,
      currentOrgId.value,
    ),
  );

  const isReportOwner = computed(
    () =>
      !isReportSaved.value ||
      getReportIsOwner(report.value, currentUserId.value, currentOrgId.value),
  );

  const canAddNotes = computed(() => {
    if (!isReportSaved.value) return false;
    if (isReportOwner.value || isPublicReport.value || isAdmin.value)
      return true;
    return (
      userAccessLevel.value === 'commenter' ||
      userAccessLevel.value === 'editor' ||
      userAccessLevel.value === 'manager'
    );
  });

  const canEditReport = computed(() => {
    if (isReportOwner.value || isAdmin.value) return true;
    if (
      userAccessLevel.value === 'commenter' ||
      userAccessLevel.value === 'viewer'
    )
      return false;
    return (
      isPublicReport.value ||
      userAccessLevel.value === 'editor' ||
      userAccessLevel.value === 'manager'
    );
  });

  const canDeleteReport = computed(() => {
    if (isReportOwner.value || isAdmin.value) return true;
    if (
      userAccessLevel.value === 'commenter' ||
      userAccessLevel.value === 'viewer'
    )
      return false;
    return (
      isPublicReport.value ||
      userAccessLevel.value === 'editor' ||
      userAccessLevel.value === 'manager'
    );
  });

  const canShareReport = computed(
    () =>
      hasScope('write:share-reports') &&
      (isReportOwner.value ||
        userAccessLevel.value === 'manager' ||
        isAdmin.value),
  );

  return {
    userAccessLevel,
    isReportOwner,
    currentUserAccess,
    canAddNotes,
    canEditReport,
    canDeleteReport,
    canShareReport,
  };
};
